/**
 * ShiftDialog Component
 * Dialog for closing shifts with cash reconciliation
 */

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Square, Clock, Loader2 } from "lucide-react";

export function ShiftDialog({ 
  open, 
  onOpenChange, 
  currentShift,
  closingCash,
  onClosingCashChange,
  bankDeposit,
  onBankDepositChange,
  floatRetained,
  onFloatRetainedChange,
  shiftNotes,
  onShiftNotesChange,
  onCloseShift,
  shiftLoading,
  formatCurrency,
  formatDate
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="shift-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Square className="h-5 w-5" />
            Close Shift
          </DialogTitle>
          <DialogDescription>
            Complete your shift with cash count and reconciliation
          </DialogDescription>
        </DialogHeader>
        
        {currentShift ? (
          <div className="space-y-4">
            <div className="bg-muted p-3 rounded-lg space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Shift #:</span>
                <span className="font-medium">{currentShift.shift_number || 1}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Opening Cash:</span>
                <span className="font-mono">{formatCurrency(currentShift.opening_cash)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Started:</span>
                <span>{formatDate(currentShift.start_time)}</span>
              </div>
            </div>
            
            <div>
              <Label>Closing Cash *</Label>
              <Input
                type="number"
                value={closingCash}
                onChange={(e) => onClosingCashChange(e.target.value)}
                placeholder="Count your cash drawer"
                data-testid="closing-cash-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Bank Deposit</Label>
                <Input
                  type="number"
                  value={bankDeposit}
                  onChange={(e) => onBankDepositChange(e.target.value)}
                  placeholder="0"
                  data-testid="bank-deposit-input"
                />
              </div>
              <div>
                <Label>Float Retained</Label>
                <Input
                  type="number"
                  value={floatRetained}
                  onChange={(e) => onFloatRetainedChange(e.target.value)}
                  placeholder="For next shift"
                  data-testid="float-retained-input"
                />
              </div>
            </div>
            
            <div>
              <Label>Notes</Label>
              <Textarea
                value={shiftNotes}
                onChange={(e) => onShiftNotesChange(e.target.value)}
                placeholder="Any notes..."
                data-testid="shift-notes-input"
              />
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <Clock className="h-8 w-8 mx-auto mb-2" />
            <p>No active shift found</p>
          </div>
        )}
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="shift-cancel-btn">
            Cancel
          </Button>
          <Button 
            variant="destructive" 
            onClick={onCloseShift} 
            disabled={!currentShift || !closingCash || shiftLoading}
            data-testid="close-shift-btn"
          >
            {shiftLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Square className="h-4 w-4 mr-2" />}
            Close Shift
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
